import { GatewaySettings } from '@/features/commerce/commerce';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Search, Sparkles, UserCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/misc';
import { useSession } from '@/features/auth/use-session';
import { useDebounce } from '@/hooks/use-debounce';
import { apiGet, apiList, apiPatch, ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { PlanTier, TenantStatus } from '@/lib/types';

type Tenant = {
  id: string;
  name: string;
  slug: string | null;
  email: string | null;
  phone: string | null;
  industry: string | null;
  teamSize: string | null;
  emirate: string | null;
  plan: PlanTier;
  subscriptionStatus: TenantStatus;
  trialEndsAt: string | null;
  seatLimit: number | null;
  createdAt: string;
  _count: { users: number; contacts: number; deals: number };
};

type Stats = {
  tenants: number;
  newTenants30d: number;
  users: number;
  activeUsers30d: number;
  contacts: number;
  deals: number;
  byPlan: Partial<Record<PlanTier, number>>;
  byStatus: Partial<Record<TenantStatus, number>>;
};

const PLANS: PlanTier[] = ['TRIAL', 'FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];
const STATUSES: TenantStatus[] = ['TRIALING', 'ACTIVE', 'PAST_DUE', 'SUSPENDED', 'CANCELLED'];

const STATUS_VARIANT: Record<TenantStatus, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  TRIALING: 'default',
  ACTIVE: 'success',
  PAST_DUE: 'warning',
  SUSPENDED: 'destructive',
  CANCELLED: 'secondary',
};

const label = (value: string) => value.charAt(0) + value.slice(1).toLowerCase().replace('_', ' ');

/** The operator console: every customer workspace on the platform. */
export function PlatformPage() {
  const { data: me } = useSession();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const q = useDebounce(search, 300);

  const stats = useQuery({
    queryKey: ['platform', 'stats'],
    queryFn: () => apiGet<Stats>('/platform/stats'),
    enabled: Boolean(me?.isPlatformAdmin),
  });

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', page, q],
    queryFn: () =>
      apiList<Tenant[]>('/platform/tenants', { params: { page, limit: 25, ...(q ? { q } : {}) } }),
    placeholderData: keepPreviousData,
    enabled: Boolean(me?.isPlatformAdmin),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; plan?: PlanTier; subscriptionStatus?: TenantStatus }) =>
      apiPatch(`/platform/tenants/${id}`, body),
    onSuccess: () => {
      toast.success('Workspace updated');
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: (error) => toast.error(error instanceof ApiRequestError ? error.message : 'Could not update'),
  });

  if (me && !me.isPlatformAdmin) return <Navigate to="/dashboard" replace />;

  const columns: Column<Tenant>[] = [
    {
      key: 'name',
      header: 'Workspace',
      cell: (t) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{t.name}</p>
          <p className="truncate text-xs text-muted-foreground">{t.email ?? t.slug}</p>
        </div>
      ),
    },
    {
      key: 'industry',
      header: 'Industry',
      cell: (t) => (
        <span className="text-sm text-muted-foreground">
          {t.industry ? label(t.industry) : '—'}
          {t.emirate ? ` · ${t.emirate}` : ''}
        </span>
      ),
    },
    {
      key: 'usage',
      header: 'Users / Contacts / Deals',
      cell: (t) => (
        <span className="tabular text-sm">
          {t._count.users} / {t._count.contacts} / {t._count.deals}
        </span>
      ),
    },
    {
      key: 'plan',
      header: 'Plan',
      cell: (t) => (
        <Select
          value={t.plan}
          className="h-8 w-36"
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => update.mutate({ id: t.id, plan: event.target.value as PlanTier })}
          disabled={update.isPending}
          aria-label={`Plan for ${t.name}`}
        >
          {PLANS.map((plan) => (
            <option key={plan} value={plan}>
              {label(plan)}
            </option>
          ))}
        </Select>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (t) => (
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[t.subscriptionStatus]}>{label(t.subscriptionStatus)}</Badge>
          <Select
            value={t.subscriptionStatus}
            className="h-8 w-32"
            onChange={(event) => {
              const next = event.target.value as TenantStatus;
              if (
                (next === 'SUSPENDED' || next === 'CANCELLED') &&
                !window.confirm(`Sign everyone in ${t.name} out and block access?`)
              ) {
                return;
              }
              update.mutate({ id: t.id, subscriptionStatus: next });
            }}
            disabled={update.isPending || t.id === me?.organizationId}
            aria-label={`Status for ${t.name}`}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {label(status)}
              </option>
            ))}
          </Select>
        </div>
      ),
    },
    {
      key: 'trial',
      header: 'Trial ends',
      cell: (t) => <span className="text-sm">{t.plan === 'TRIAL' ? formatDate(t.trialEndsAt) : '—'}</span>,
    },
    {
      key: 'created',
      header: 'Signed up',
      cell: (t) => <span className="text-sm text-muted-foreground">{formatDate(t.createdAt)}</span>,
    },
  ];

  const s = stats.data;

  return (
    <div className="space-y-6">
      <PageHeader title="Platform" description="Every company workspace on DovoCRM." />

      <GatewaySettings scope="platform" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Building2} label="Workspaces" value={s?.tenants} hint={s ? `+${s.newTenants30d} in 30 days` : undefined} />
        <StatCard icon={Sparkles} label="On trial" value={s?.byStatus.TRIALING ?? (s ? 0 : undefined)} hint={s ? `${s.byStatus.ACTIVE ?? 0} paying` : undefined} />
        <StatCard icon={Users} label="Users" value={s?.users} hint={s ? `${s.activeUsers30d} active in 30 days` : undefined} />
        <StatCard icon={UserCheck} label="Contacts / Deals" value={s ? `${s.contacts} / ${s.deals}` : undefined} />
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search workspaces…"
          className="ps-9"
        />
      </div>

      <DataTable
        columns={columns}
        rows={tenants.data?.data ?? []}
        rowKey={(t) => t.id}
        isLoading={tenants.isLoading}
        error={tenants.error ? 'Could not load workspaces' : null}
        onRetry={() => void tenants.refetch()}
        meta={tenants.data?.meta}
        onPageChange={setPage}
        emptyTitle="No workspaces found"
      />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Building2;
  label: string;
  value: number | string | undefined;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="tabular mt-1.5 text-2xl font-extrabold tracking-tight">{value ?? '—'}</p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
      </CardContent>
    </Card>
  );
}
